<!--
  @component
  Implement the editor for an Object field.
  @see https://decapcms.org/docs/widgets/#Object
  @see https://sveltiacms.app/en/docs/fields/object
-->
<script>
  import { _ } from '@sveltia/i18n';
  import { Button, Checkbox, Icon } from '@sveltia/ui';
  import { toRaw } from '@sveltia/utils/object';
  import { getContext, onMount, tick } from 'svelte';

  import AddItemButton from '$lib/components/contents/details/fields/object/add-item-button.svelte';
  import ObjectBody from '$lib/components/contents/details/fields/object/object-body.svelte';
  import ObjectHeader from '$lib/components/contents/details/fields/object/object-header.svelte';
  import { suspendAutoDuplication } from '$lib/services/contents/draft';
  import { getDefaultValues } from '$lib/services/contents/draft/defaults';
  import { getEntryDraftContext } from '$lib/services/contents/draft/state.svelte';
  import {
    copyDefaultLocaleValues,
    forEachTargetLocale,
  } from '$lib/services/contents/draft/update/locale';
  import { getValueMapSnapshot } from '$lib/services/contents/draft/value-map.svelte';
  import {
    getInitialExpanderState,
    syncExpanderStates,
  } from '$lib/services/contents/editor/fields';
  import { getKeysByPrefix } from '$lib/services/contents/entry/key-paths';
  import {
    formatSummary,
    getUnknownTypeMessage,
  } from '$lib/services/contents/fields/object/helpers';
  import { getObjectThumbnail } from '$lib/services/contents/fields/object/thumbnail';

  /**
   * @import {
   * EntryDraft,
   * FieldEditorContext,
   * FieldEditorProps,
   * MediaFieldSource,
   * } from '$lib/types/private';
   * @import {
   * ObjectField,
   * ObjectFieldWithSubFields,
   * ObjectFieldWithTypes,
   * } from '$lib/types/public';
   */

  /**
   * @typedef {object} Props
   * @property {ObjectField} fieldConfig Field configuration.
   * @property {object | undefined} currentValue Field value.
   */

  const entryDraft = getEntryDraftContext();

  /** @type {FieldEditorContext} */
  const {
    fieldContext,
    valueStoreKey = 'currentValues',
    parentComponentNames = [],
  } = getContext('field-editor') ?? {};
  const componentName = parentComponentNames.at(-1);
  // Hide the header/expander if in a single subfield list field because it’s redundant
  const hideHeader = fieldContext === 'single-subfield-list-field';

  /** @type {FieldEditorProps & Props} */
  let {
    /* eslint-disable prefer-const */
    locale,
    keyPath,
    typedKeyPath,
    fieldLabel,
    fieldConfig,
    required = true,
    /* eslint-enable prefer-const */
  } = $props();

  const fieldId = $props.id();

  const {
    name: fieldName,
    i18n = false,
    // Field type-specific options
    collapsed,
    summary,
    thumbnail: thumbnailFieldName,
  } = $derived(fieldConfig);
  const { fields } = $derived(/** @type {ObjectFieldWithSubFields} */ (fieldConfig));
  const { types, typeKey = 'type' } = $derived(/** @type {ObjectFieldWithTypes} */ (fieldConfig));
  /* v8 ignore start -- the editor is only rendered while the draft is there */
  const isIndexFile = $derived(entryDraft.current?.isIndexFile ?? false);
  const collectionName = $derived(entryDraft.current?.collectionName ?? '');
  /* v8 ignore stop */
  const fileName = $derived(entryDraft.current?.fileName);
  const defaultLocale = $derived(entryDraft.current?.defaultLocale);
  const valueMap = $derived(getValueMapSnapshot(entryDraft.current, locale, valueStoreKey));
  const getFieldArgs = $derived({ collectionName, fileName, valueMap, isIndexFile });
  // Ask the shared key path index rather than scanning the whole value map: the editor renders one
  // component per field, and a large entry would otherwise be walked once per Object field on
  // every keystroke
  const hasValues = $derived(
    getKeysByPrefix(valueMap, `${keyPath}.`).some((_keyPath) => valueMap[_keyPath] !== undefined),
  );
  const canEdit = $derived(
    fieldContext === 'rich-text-editor-component' || locale === defaultLocale || i18n !== false,
  );
  const parentExpandedKeyPath = $derived(`${keyPath}#`);
  const parentExpanded = $derived(
    entryDraft.current?.expanderStates?._[parentExpandedKeyPath] ?? true,
  );
  const hasVariableTypes = $derived(Array.isArray(types));
  const typeKeyPath = $derived(`${keyPath}.${typeKey}`);
  /* v8 ignore start -- only read for an object with variable types */
  const type = $derived(hasVariableTypes ? valueMap[typeKeyPath] : undefined);
  /* v8 ignore stop */
  const typeConfig = $derived(type ? types?.find(({ name }) => name === type) : undefined);
  const unknownType = $derived(hasVariableTypes && !typeConfig);
  const subFields = $derived((hasVariableTypes ? typeConfig?.fields : fields) ?? []);
  const summaryTemplate = $derived(hasVariableTypes ? typeConfig?.summary || summary : summary);
  const addButtonDisabled = $derived(locale !== defaultLocale && i18n === 'duplicate');

  /**
   * Initialize the expander state.
   */
  const initializeExpanderState = () => {
    const draft = entryDraft.current;

    if (hideHeader || !draft) {
      return;
    }

    const key = parentExpandedKeyPath;

    syncExpanderStates({
      draft,
      stateMap: { [key]: getInitialExpanderState({ draft, key, locale, collapsed }) },
    });
  };

  /**
   * Add the object’s subfields to the entry draft with the default values populated.
   * @param {object} [args] Arguments.
   * @param {string} [args.type] Variable type name. If the field doesn’t have variable types, it
   * will be `undefined`.
   * @returns {Promise<void>} A promise that resolves once the fields have been added.
   */
  const addFields = async ({ type: _type } = {}) =>
    // Avoid triggering the Proxy’s i18n duplication strategy for descendant fields. The suspension
    // has to span the `await` below, because the values are written after it
    suspendAutoDuplication(async () => {
      const draft = entryDraft.current;

      /* v8 ignore next 3 -- the button is only offered while the draft is there */
      if (!draft) {
        return;
      }

      if (_type) {
        forEachTargetLocale({ valueStore: draft[valueStoreKey], locale, i18n }, (_valueMap) => {
          _valueMap[typeKeyPath] = _type;
        });

        // Wait until `subFields` is updated
        await tick();
      }

      const newContent = Object.fromEntries(
        Object.entries(
          getDefaultValues({ fields: subFields, locale, defaultLocale: draft.defaultLocale }),
        ) //
          .map(([_keyPath, value]) => [`${keyPath}.${_keyPath}`, value]),
      );

      const newValueMap =
        locale === defaultLocale
          ? newContent
          : copyDefaultLocaleValues({
              draft,
              content: newContent,
              targetLanguage: locale,
              keyPathPrefix: keyPath,
            });

      forEachTargetLocale({ valueStore: draft[valueStoreKey], locale, i18n }, (_valueMap) => {
        // Apply the new values through the Proxy
        Object.assign(_valueMap, toRaw({ ...newValueMap, ..._valueMap }));

        // Disable validation
        delete _valueMap[keyPath];
      });
    });

  /**
   * Remove the object’s subfields from the entry draft.
   */
  const removeFields = () => {
    forEachTargetLocale(
      { valueStore: entryDraft.current?.[valueStoreKey], locale, i18n },
      (_valueMap) => {
        // Assign `null` before deleting each property, so the draft proxy can revalidate the field.
        // The value map is the draft’s live map, which is mutated right below, so its key paths
        // have to be read as they are right now
        getKeysByPrefix(_valueMap, `${keyPath}.`, { live: true }).forEach((_keyPath) => {
          _valueMap[_keyPath] = null;
          delete _valueMap[_keyPath];
        });

        // Enable validation
        _valueMap[keyPath] = null;
      },
    );
  };

  /**
   * Format the summary template.
   * @returns {string} Formatted summary.
   */
  const _formatSummary = () => formatSummary({ ...getFieldArgs, keyPath, locale, summaryTemplate });

  /**
   * Get the thumbnail of the collapsed object.
   * @returns {MediaFieldSource | undefined} Thumbnail.
   */
  const getThumbnail = () =>
    getObjectThumbnail({
      thumbnailFieldName,
      keyPath,
      typedKeyPath: type ? `${typedKeyPath}<${type}>` : typedKeyPath,
      collectionName,
      fileName,
      componentName,
      valueMap,
      isIndexFile,
      entry: entryDraft.current?.originalEntry,
      files: entryDraft.current?.files,
    });

  /**
   * Warn about unknown variable type.
   */
  const warnUnknownType = () => {
    const message = getUnknownTypeMessage({ fieldType: 'object', type, typeKey, types });

    // eslint-disable-next-line no-console
    console.warn(`List item ${keyPath}: ${message}`);
  };

  onMount(() => {
    initializeExpanderState();

    if (canEdit && hasValues && unknownType) {
      warnUnknownType();
    }
  });
</script>

{#if !hasVariableTypes && !required}
  <Checkbox
    label={_('add_x', { values: { name: fieldLabel || fieldName } })}
    checked={hasValues}
    disabled={addButtonDisabled}
    onChange={({ detail: { checked } }) => {
      if (checked) {
        addFields();
      } else {
        removeFields();
      }
    }}
  />
{/if}

{#if hasVariableTypes && !hasValues}
  <AddItemButton disabled={addButtonDisabled} {fieldConfig} addItem={addFields} />
{/if}

{#if (!(!required || hasVariableTypes) || hasValues) && canEdit}
  <div
    role="group"
    class="wrapper"
    class:unknown-type={unknownType}
    class:expanded={parentExpanded}
    aria-labelledby={parentExpanded ? undefined : `object-${fieldId}-summary`}
  >
    {#if !hideHeader}
      <ObjectHeader
        label={hasVariableTypes ? typeConfig?.label || type : ''}
        controlId="object-{fieldId}-item-list"
        expanded={parentExpanded}
        toggleExpanded={subFields.length
          ? () =>
              syncExpanderStates({
                draft: /** @type {EntryDraft} */ (entryDraft.current),
                stateMap: { [parentExpandedKeyPath]: !parentExpanded },
              })
          : undefined}
      >
        {#snippet endContent()}
          {#if hasVariableTypes}
            <Button
              size="small"
              iconic
              disabled={addButtonDisabled}
              aria-label={_('remove')}
              onclick={() => {
                removeFields();
              }}
            >
              {#snippet startIcon()}
                <Icon name="close" />
              {/snippet}
            </Button>
          {/if}
        {/snippet}
      </ObjectHeader>
    {/if}
    <div role="none" class="item-list" id="object-{fieldId}-item-list">
      <ObjectBody
        {locale}
        {subFields}
        getSubFieldProps={(subField) => ({
          keyPath: `${keyPath}.${subField.name}`,
          typedKeyPath:
            hasVariableTypes && type
              ? `${typedKeyPath}<${type}>.${subField.name}`
              : `${typedKeyPath}.${subField.name}`,
        })}
        expanded={parentExpanded}
        {unknownType}
        getSummary={_formatSummary}
        {getThumbnail}
        summaryId="object-{fieldId}-summary"
      />
    </div>
  </div>
{/if}

<style>
  .wrapper {
    border-inline-width: 2px;
    border-color: var(--sui-secondary-border-color);
    border-radius: var(--sui-control-medium-border-radius);

    &.expanded,
    &:has(:global(.summary)) {
      border-bottom-width: 2px;
    }

    &.unknown-type {
      overflow: hidden;

      :global(.alert) {
        border-width: 0;
        border-radius: 0;
      }
    }
  }

  :global(.sui.checkbox) + .wrapper {
    & > :global(.group) {
      margin-top: 8px;
    }
  }
</style>
